import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Settings, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { supabase } from '../config/supabase';
import { toast } from '../components/ui/use-toast';

interface UserMenuProps {
  isMobile?: boolean;
}

export function UserMenu({ isMobile = false }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isTrader, setIsTrader] = useState(false);
  const [isCraftie, setIsCraftie] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { adminUser, loading: adminLoading } = useAdminAuth();
  const isAdmin = !!adminUser;

  useEffect(() => {
    const checkUserType = async () => {
      if (!user) {
        setIsTrader(false);
        setIsCraftie(false);
        return;
      }

      try {
        // Check if user is a trader
        const { data: traderData, error: traderError } = await supabase
          .from('traders')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (traderError) {
          console.warn('Trader check error:', traderError);
        }

        // Check if user is a craftie
        const { data: craftieData, error: craftieError } = await supabase
          .from('crafties')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (craftieError) {
          console.warn('Craftie check error:', craftieError);
        }

        setIsTrader(!!traderData);
        setIsCraftie(!!craftieData);
      } catch (err) {
        console.warn('UserMenu role detection failed:', err);
        setIsTrader(false);
        setIsCraftie(false);
      }
    };

    checkUserType();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuClick = async (path: string) => {
    const role = user?.user_metadata?.role;

    if (path === '/admin') {
      if (adminLoading) {
        toast({
          title: 'Please wait',
          description: 'Checking admin permissions...'
        });
        return;
      }
      // Check if user is an admin
      if (!isAdmin) {
        toast({
          title: 'Access Denied',
          description: 'You do not have admin privileges.',
          variant: 'destructive'
        });
        return;
      }

      navigate(path);
    } else if (path === '/dashboard') {
      // Generic dashboard entry that routes to the unified user dashboard
      if (isAdmin || role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } else if (path === '/craftie/dashboard') {
      if (!isCraftie) {
        toast({
          title: 'Incomplete Profile',
          description: 'Please complete your craftsman profile first.',
          variant: 'destructive'
        });
        navigate('/register/craftsman');
        return;
      }

      navigate(path);
    } else if (path === '/trader/dashboard') {
      // Check if user is a trader
      if (!isTrader) {
        // If no trader found, show an error or redirect to registration
        toast({
          title: 'Incomplete Profile',
          description: 'Please complete your trader profile first.',
          variant: 'destructive'
        });
        navigate('/trader/register');
        return;
      }

      navigate(path);
    } else {
      navigate(path);
    }
    setIsOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    setIsOpen(false);
    navigate('/');
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-full hover:bg-gray-100 transition-colors"
        aria-label="User menu"
      >
        <User className="w-6 h-6 text-gray-600" />
      </button>

      {isOpen && (
        <div className={`absolute mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50 ${isMobile ? 'left-1/2 -translate-x-1/2' : 'right-0'}`}>
          {user ? (
            <>
              <div className="px-4 py-2 text-sm text-gray-500 border-b">
                {user.email}
              </div>
              <button
                onClick={() => handleMenuClick('/dashboard')}
                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <Settings className="w-4 h-4 mr-2" />
                Dashboard
              </button>
              {isAdmin && (
                <button
                  onClick={() => handleMenuClick('/admin')}
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Admin Panel
                </button>
              )}
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => handleMenuClick('/login')}
                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <User className="w-4 h-4 mr-2" />
                Sign In
              </button>
              <button
                onClick={() => handleMenuClick('/register/user')}
                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <User className="w-4 h-4 mr-2" />
                Sign Up
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
